import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Search, MessageSquare, ChevronLeft, ChevronRight, } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
} from '../components/ui/Table';
import { Badge } from '../components/ui/Badge';
import { customerAPI } from '../services/ApiServices';
import { ExportOptions } from '../components/common/ExportOptions';
import { useExport } from '../hooks/useExport';

export const Resolved = ({ type }) => {
  const [customers, setCustomers] = useState([]);
  const [exportCustomers, setExportCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isExporting, setIsExporting] = useState(false);
  const [pagination, setPagination] = useState(null);
  const limit = 10;
  const navigate = useNavigate();
  const exportConfig = useMemo(() => {
    if (type === 'approved-overridden') {
      return {
        fileKey: 'override_customers',
        reportName: 'Override Customers Report'
      };
    }

    if (type === 'resolved') {
      return {
        fileKey: 'resolved_customers',
        reportName: 'Resolved Customers Report'
      };
    }

    return {
      fileKey: 'default_export',
      reportName: 'Default Report'
    };
  }, [type]);

  const { handleExport } = useExport(
    exportConfig.fileKey,
    exportConfig.reportName
  );



  /* =========================
     Fetch Data
  ========================= */

  useEffect(() => {
    setCurrentPage(1);
  }, [type]);

  useEffect(() => {
    const delay = setTimeout(() => {
      fetchCustomers();
      fetchAllResolvedCustomers();
    }, 300); // debounce

    return () => clearTimeout(delay);
  }, [currentPage, searchTerm, type]);

  const fetchCustomers = async () => {
    setIsLoading(true);

    try {
      console.log(type);

      const response =
        type === 'approved-overridden'
          ? (
            await customerAPI.getApprovedOverriddenCustomers({
              page: currentPage,
              limit,
              searchTerm,
            })
          )
          : await customerAPI.getVerifiedCustomers({
            page: currentPage,
            limit,
            searchTerm,
          });

      const { data, pagination } = response;
      setCustomers(data);
      setFilteredCustomers(data);
      setPagination(pagination);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
      setIsLoading(false)
    } finally {
      setIsLoading(false);
    }
  };


  const fetchAllResolvedCustomers = async () => {
    setIsExporting(true);
    try {


      const response =
        type === 'approved-overridden'
          ? (
            await customerAPI.getApprovedOverriddenCustomers({
              searchTerm,
            })
          )
          : await customerAPI.getVerifiedCustomers({
            searchTerm,
          });

      const { data } = response;
      setExportCustomers(data);
      // const { data } = await customerAPI.getVerifiedCustomers({ search: searchTerm });
      return data;
    } catch (error) {
      console.error('Failed to fetch all resolved customers for export:', error);
      throw error;
    } finally {
      setIsExporting(false);
    }
  };

  /* =========================
     Search
  ========================= */
  // useEffect(() => {
  //   if (!searchTerm.trim()) {
  //     setFilteredCustomers(customers);
  //     return;
  //   }

  //   const term = searchTerm.toLowerCase();

  //   const filtered = customers.filter((item) => {
  //     return (
  //       item.ofsData?.fullName?.toLowerCase().includes(term) ||
  //       item.ofsData?.customerId?.toLowerCase().includes(term) ||
  //       item.referenceNumber?.toLowerCase().includes(term)
  //     );
  //   });

  //   setFilteredCustomers(filtered);
  // }, [searchTerm, customers]);

  /* =========================
     Helpers
  ========================= */
  const formatDate = (date) =>
    date
      ? new Date(date).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
      : '-';

  /* =========================
     Export Config
  ========================= */
  const exportColumns = [
    {
      header: 'Sub ID',
      accessor: (item) => `'${item.faydaData?.sub || ''}`,
      width: 20,
      pdfWidth: 30,
    },
    {
      header: 'Customer ID',
      accessor: 'ofsData.customerId',
      width: 20,
      pdfWidth: 30,
    },
    {
      header: 'Name',
      accessor: 'ofsData.fullName',
      width: 25,
      pdfWidth: 40,
    },
    {
      header: 'Email',
      accessor: 'ofsData.email',
      width: 30,
      pdfWidth: 45,
    },
    {
      header: 'Phone',
      accessor: 'ofsData.phone',
      width: 20,
      pdfWidth: 30,
    },
    {
      header: 'Company Code',
      accessor: 'ofsData.companyCode',
      width: 20,
      pdfWidth: 30,
    },
    {
      header: 'Status',
      accessor: 'status',
      width: 12,
      pdfWidth: 20,
    },
    {
      header: 'Verified At',
      accessor: (item) => formatDate(item.reviewedAt),
      width: 20,
      pdfWidth: 35,
    },
  ];

  const onExport = async (format) => {
    // handleExport(format, customers, exportColumns);
    try {
      // Fetch all pending customers for export
      const allCustomers = await fetchAllResolvedCustomers();

      console.log("All Customers: ", allCustomers)

      // Use your export hook with all data
      // Assuming your useExport hook can be called directly:
      handleExport(format, allCustomers, exportColumns);
    } catch (error) {
      console.error('Export failed:', error);
      // You might want to show an error toast here
    }
  };
  /* =========================
     Loading
  ========================= */
  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  /* =========================
     Render
  ========================= */
  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <CheckCircle className="w-8 h-8 text-green-600" />
              {type === 'approved-overridden' ?
                <h1 className="text-3xl font-bold text-gray-900">Approved and Overide</h1>
                :
                (<h1 className="text-3xl font-bold text-gray-900">Resolved</h1>)

              }
            </div>
            <p className="text-gray-600">
              Successfully verified and approved customers
            </p>
          </div>

          <div className="flex items-center gap-4">
            <div className="bg-green-50 px-4 py-2 rounded-lg border border-green-200">
              <p className="text-sm font-semibold text-green-800">
                {pagination.total} Resolved
              </p>
            </div>

            <ExportOptions
              data={exportCustomers}
              onExport={onExport}
              isLoading={isExporting}
              disabled={isLoading}
            />
          </div>
        </div>

        {/* Table */}
        <Card padding="none">
          {/* Search */}
          <div className="p-6 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search by name, customer ID, or reference number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full px-4 py-3 border-2 border-slate-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
              />
            </div>
          </div>

          {filteredCustomers.length > 0 ? (
            <>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeader>Sub ID</TableHeader>
                    <TableHeader>Customer ID</TableHeader>
                    <TableHeader>Name</TableHeader>
                    <TableHeader>Reference No</TableHeader>
                    <TableHeader>Status</TableHeader>
                    <TableHeader>Verified By</TableHeader>
                    <TableHeader>Verified At</TableHeader>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filteredCustomers.map((item) => (
                    <TableRow key={item._id}>
                      <TableCell>
                        <span className="font-mono text-xs font-semibold">
                          {item.faydaData.sub}
                        </span>
                      </TableCell>
                      <TableCell className="font-mono text-xs font-semibold">
                        {item.ofsData?.customerId}
                      </TableCell>

                      <TableCell className="font-medium">
                        {item.ofsData?.fullName}
                      </TableCell>

                      <TableCell className="font-mono text-xs">
                        {item.referenceNumber}
                      </TableCell>

                      <TableCell>
                        <Badge status={item.status} />
                      </TableCell>

                      <TableCell className="text-gray-600">
                        {item.reviewedBy
                          ? `${item.reviewedBy.firstName} ${item.reviewedBy.lastName}`
                          : '-'}
                      </TableCell>

                      <TableCell className="text-gray-600">
                        {formatDate(item.reviewedAt)}
                      </TableCell>

                      <TableCell>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => navigate(`/approved/:${item.ofsData?.customerId}`, { state: { customer: item } })}
                          className="gap-2"
                        >
                          <MessageSquare className="w-4 h-4" />
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {pagination && pagination.total > 0 && (
                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">

                  {/* Info */}
                  <div className="text-sm text-gray-600">
                    Showing {(currentPage - 1) * limit + 1} to{' '}
                    {Math.min(currentPage * limit, pagination.total)} of{' '}
                    {pagination.total} results
                  </div>

                  {/* Controls */}
                  <div className="flex items-center gap-2">
                    {/* Previous */}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Previous
                    </Button>

                    {/* Page Numbers */}
                    <div className="flex gap-1">
                      {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(page => (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`px-3 py-1 rounded-lg text-sm font-medium
              ${page === currentPage
                              ? 'bg-blue-600 text-white'
                              : 'text-gray-600 hover:bg-gray-100'
                            }`}
                        >
                          {page}
                        </button>
                      ))}
                    </div>

                    {/* Next */}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        setCurrentPage(p => Math.min(pagination.pages, p + 1))
                      }
                      disabled={currentPage === pagination.pages}
                    >
                      Next
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="p-12 text-center">
              {searchTerm ? (
                <>
                  <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    No Results Found
                  </h3>
                  <p className="text-gray-600">
                    No customers match your search criteria
                  </p>
                </>
              ) : (
                <>
                  <CheckCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    No Resolved Customers
                  </h3>
                  <p className="text-gray-600">
                    Approved customers will appear here
                  </p>
                </>
              )}
            </div>
          )}
        </Card>
      </div>
    </Layout>
  );
};
